import { Link } from "react-router-dom";
import { Gauge, Sparkles, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";

const scoreColor = (s) => (s >= 70 ? "text-emerald-500" : s >= 50 ? "text-accent" : s >= 35 ? "text-amber-500" : "text-red-500");
const fmt = (n) => (n || n === 0 ? `$${Number(n).toLocaleString()}` : "—");

const fmtMetric = (k, mv) => {
  if (k.endsWith("_pct")) return `${mv > 0 ? "+" : ""}${mv}%`;
  if (k === "days_on_market") return `${mv}d`;
  if (k.includes("ppsf")) return `$${Number(mv).toLocaleString()}`;
  return fmt(mv);
};

export default function InvestmentValuation({ val, metricLabels, unavailableNote = "Verified band stats unavailable — no score can be computed." }) {
  return (
    <div className="bg-surface border border-border rounded-xl p-5" data-testid="valuation-block">
      <div className="flex items-center gap-2 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mb-3">
        <Gauge className="w-3.5 h-3.5" strokeWidth={1.5} />Investment Valuation
      </div>
      {!val ? (
        <div className="font-mono-data text-sm text-muted-foreground animate-pulse">Computing…</div>
      ) : val.locked ? (
        <div data-testid="valuation-locked" className="relative overflow-hidden rounded-lg">
          <div className="pointer-events-none select-none blur-[6px] opacity-50" aria-hidden>
            <div className="flex items-center gap-4">
              <div className="font-mono-data text-4xl leading-none text-emerald-500">87</div>
              <div><div className="font-display text-lg font-medium text-emerald-500">Strong Value</div>
                <div className="text-[11px] text-muted-foreground">Estimate from verified market bands</div></div>
            </div>
            <div className="space-y-2 mt-4">
              {["Fair value", "Confidence", "Comps", "Price vs. median"].map((l) => (
                <div key={l} className="flex items-center justify-between">
                  <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{l}</span>
                  <span className="font-mono-data text-xs">$0,000,000</span>
                </div>))}
            </div>
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-3 bg-background/20">
            <div className="w-10 h-10 rounded-full bg-accent/15 border border-accent/40 flex items-center justify-center"><Lock className="w-4 h-4 text-accent" /></div>
            <p className="font-mono-data text-[10px] uppercase tracking-wider text-foreground/80">Upgrade to Vaelith Membership to unlock this feature</p>
            <Link to="/pricing"><Button size="sm" className="rounded-full mt-1" data-testid="valuation-upgrade"><Sparkles className="w-3.5 h-3.5 mr-1.5" />Upgrade</Button></Link>
          </div>
        </div>
      ) : !val.available ? (
        <div data-testid="valuation-unavailable">
          <p className="font-mono-data text-xs text-muted-foreground leading-relaxed">
            {val.reason === "insufficient_band"
              ? `No verifiable market data${val.band ? ` for the ${val.band} band` : ""} yet — we won't show a potentially misleading estimate.`
              : unavailableNote}
          </p>
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-4">
            <div className={`font-mono-data text-4xl leading-none ${scoreColor(val.investment_score)}`} data-testid="valuation-score">{val.investment_score}</div>
            <div>
              <div className={`font-display text-lg font-medium ${scoreColor(val.investment_score)}`} data-testid="valuation-verdict">{val.verdict}</div>
              <div className="text-[11px] text-muted-foreground">Estimate from verified market bands</div>
            </div>
          </div>
          {val.band && (
            <div className="mt-3 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground" data-testid="valuation-band">
              Band · {val.band}{val.band_sufficient === false ? " · limited sample" : ""}
            </div>
          )}
          <div className="space-y-2 mt-4">
            {Object.entries(val.metrics || {}).filter(([k, mv]) => mv != null && k !== "band").map(([k, mv]) => (
              <div key={k} className="flex items-center justify-between">
                <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{metricLabels[k] || k}</span>
                <span className="font-mono-data text-xs">{fmtMetric(k, mv)}</span>
              </div>
            ))}
          </div>
          {val.ai_narrative && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center gap-1.5 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mb-2"><Sparkles className="w-3.5 h-3.5" />AI Analyst</div>
              <p className="text-sm leading-relaxed" data-testid="valuation-narrative">{val.ai_narrative}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
